// === IMPORTS ===
import React, { useState } from 'react';
import { AIAgent, AgentInfoCategory } from '../types';
import { AGENT_ROLES_CONFIG } from '../data/officeCatalog';
import { getAgentFullDetails } from '../utils/agentDetailsHelper';
import AppIcon from './AppIcon';
import {
  X, Battery, Coffee, Droplets, Zap, Cpu, ClipboardList, Target, Brain,
  Wrench, BookOpen, Bot, Coins, Activity, Lightbulb, Network, CheckCircle2,
  Clock, ArrowUpRight, ArrowDownRight, Copy, Check, Sparkles,
} from 'lucide-react';
import '../styles/twin-modals-shared.css';
import '../styles/twin-icons-shared.css';
import './AgentModal.css';

interface AgentModalProps {
  agent: AIAgent;
  allAgents?: AIAgent[];
  initialTab?: AgentInfoCategory;
  onClose: () => void;
  onForceBreak: (agentId: string, type: 'coffee' | 'bathroom' | 'work') => void;
  onRunCustomTask: (agentId: string, taskTitle: string, context: string) => Promise<void>;
  isProcessingAI: boolean;
}

const TAB_CONFIG: { id: AgentInfoCategory; label: string; icon: React.ReactNode }[] = [
  { id: 'task', label: 'Task', icon: <ClipboardList className="twin-icon-sm" /> },
  { id: 'goal', label: 'Goal', icon: <Target className="twin-icon-sm" /> },
  { id: 'context', label: 'Context', icon: <Brain className="twin-icon-sm" /> },
  { id: 'tools', label: 'Tools', icon: <Wrench className="twin-icon-sm" /> },
  { id: 'knowledge', label: 'Knowledge', icon: <BookOpen className="twin-icon-sm" /> },
  { id: 'model', label: 'Model', icon: <Bot className="twin-icon-sm" /> },
  { id: 'cost', label: 'Cost', icon: <Coins className="twin-icon-sm" /> },
  { id: 'state', label: 'State', icon: <Activity className="twin-icon-sm" /> },
  { id: 'decisions', label: 'Decisions', icon: <Lightbulb className="twin-icon-sm" /> },
  { id: 'dependencies', label: 'Dependencies', icon: <Network className="twin-icon-sm" /> },
];

// === COMPONENTE: MODAL DE DETALLE COMPLETO DEL AGENTE (10 PESTAÑAS) ===
export const AgentModal: React.FC<AgentModalProps> = ({
  agent,
  allAgents = [],
  initialTab = 'task',
  onClose,
  onForceBreak,
  onRunCustomTask,
  isProcessingAI,
}) => {
  const [activeTab, setActiveTab] = useState<AgentInfoCategory>(initialTab);
  const [customTaskTitle, setCustomTaskTitle] = useState('');
  const [customContext, setCustomContext] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const roleConfig = AGENT_ROLES_CONFIG[agent.role];
  const fullDetails = getAgentFullDetails(agent, allAgents);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customTaskTitle.trim()) return;
    await onRunCustomTask(agent.id, customTaskTitle.trim(), customContext.trim());
    setCustomTaskTitle('');
    setCustomContext('');
  };

  // === RENDER ===
  return (
    <div className="twin-modal-overlay" onClick={onClose}>
      <div className="twin-modal twin-agentmodal" onClick={(e) => e.stopPropagation()}>
        {/* === HEADER === */}
        <div className="twin-agentmodal-header">
          <div className="twin-agentmodal-header-left">
            <div
              className="twin-agentmodal-avatar"
              style={{ backgroundColor: roleConfig?.avatarShirtColor || '#3b82f6' }}
            >
              {agent.spriteSet?.front_idle || agent.customImage ? (
                <img
                  src={agent.spriteSet?.front_idle || agent.customImage}
                  alt={agent.name}
                  className="twin-agentmodal-avatar-img"
                  style={{ imageRendering: 'pixelated' }}
                />
              ) : (
                <AppIcon icon={roleConfig?.icon} role={agent.role} className="twin-icon-lg twin-agentmodal-avatar-icon" />
              )}
            </div>
            <div>
              <div className="twin-agentmodal-name-row">
                <h3 className="twin-agentmodal-name">{agent.nickname?.trim() || agent.name}</h3>
                <span className="twin-agentmodal-tag twin-agentmodal-tag--cyan">{fullDetails.state.statusLabel}</span>
                <span className="twin-agentmodal-tag">{fullDetails.model.modelName}</span>
              </div>
              <p className="twin-agentmodal-subline">
                <span>{roleConfig?.title}</span>
                <span>•</span>
                <span className="twin-agentmodal-subline-item"><Zap className="twin-icon-xs twin-color-goal" />{fullDetails.state.energy}% Energy</span>
                <span>•</span>
                <span className="twin-agentmodal-subline-item"><Target className="twin-icon-xs twin-color-task" />{fullDetails.state.focus}% Focus</span>
                <span>•</span>
                <span className="twin-agentmodal-subline-item"><Coins className="twin-icon-xs twin-color-tools" />${fullDetails.cost.totalCostUsd} USD</span>
              </p>
            </div>
          </div>

          <button type="button" onClick={onClose} className="twin-modal-close-btn twin-agentmodal-close-btn" title="Close modal">
            <X className="twin-icon-md" />
          </button>
        </div>

        {/* === BARRA DE 10 PESTAÑAS === */}
        <div className="twin-agentmodal-tabs">
          {TAB_CONFIG.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`twin-agentmodal-tab ${isActive ? 'twin-agentmodal-tab--active' : ''}`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* === CONTENIDO PRINCIPAL === */}
        <div className="twin-agentmodal-body">
          {/* === TAB 1: TASK === */}
          {activeTab === 'task' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-card">
                <div className="twin-agentmodal-row-between">
                  <div>
                    <span className="twin-agentmodal-label twin-color-task">Active Running Task</span>
                    <h4 className="twin-agentmodal-title-lg">{fullDetails.task.title}</h4>
                    <p className="twin-agentmodal-desc">{fullDetails.task.description}</p>
                  </div>
                  <span className={`twin-agentmodal-complexity twin-agentmodal-complexity--${fullDetails.task.complexity}`}>
                    Complexity: {fullDetails.task.complexity.toUpperCase()}
                  </span>
                </div>

                <div className="twin-agentmodal-progress-block">
                  <div className="twin-agentmodal-row-between twin-agentmodal-progress-label">
                    <span>Processing Progress</span>
                    <span className="twin-color-task twin-agentmodal-mono-bold">{fullDetails.task.progress}%</span>
                  </div>
                  <div className="twin-agentmodal-progress-track">
                    <div className="twin-agentmodal-progress-fill twin-agentmodal-progress-fill--task" style={{ width: `${fullDetails.task.progress}%` }} />
                  </div>
                  <div className="twin-agentmodal-row-between twin-agentmodal-progress-footer">
                    <span>Started: {fullDetails.task.startedAtFormatted}</span>
                    <span>ETA: {fullDetails.task.etaFormatted}</span>
                  </div>
                </div>
              </div>

              <div className="twin-agentmodal-subsection">
                <h4 className="twin-agentmodal-subheading">
                  <ClipboardList className="twin-icon-sm twin-color-task" /> Assigned Task Queue ({fullDetails.task.queueCount})
                </h4>
                <div className="twin-agentmodal-list">
                  {fullDetails.task.queuedTasks.map((q, idx) => (
                    <div key={q.id} className="twin-agentmodal-queue-row">
                      <div className="twin-agentmodal-queue-left">
                        <span className="twin-agentmodal-queue-index">{idx + 1}</span>
                        <span className="twin-agentmodal-queue-title">{q.title}</span>
                      </div>
                      <div className="twin-agentmodal-queue-tags">
                        <span className="twin-agentmodal-chip">{q.complexity}</span>
                        <span className="twin-agentmodal-chip twin-agentmodal-chip--indigo">Priority: {q.priority}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {fullDetails.task.outputPreview && (
                <div className="twin-agentmodal-output-box">
                  <span className="twin-agentmodal-label twin-color-task">Latest Pipeline Log / Output:</span>
                  <p className="twin-agentmodal-output-text">{fullDetails.task.outputPreview}</p>
                </div>
              )}
            </div>
          )}

          {/* === TAB 2: GOAL === */}
          {activeTab === 'goal' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-card twin-agentmodal-card--goal">
                <span className="twin-agentmodal-label twin-color-goal"><Target className="twin-icon-sm" /> Primary Strategic Goal</span>
                <h4 className="twin-agentmodal-title-lg">{fullDetails.goal.primaryGoal}</h4>
                <p className="twin-agentmodal-desc">{fullDetails.goal.strategicPurpose}</p>
              </div>

              <div className="twin-agentmodal-subsection">
                <h4 className="twin-agentmodal-subheading">Target Performance KPIs</h4>
                <div className="twin-agentmodal-kpi-grid">
                  {fullDetails.goal.targetKpis.map((kpi, idx) => (
                    <div key={idx} className="twin-agentmodal-kpi-item">
                      <CheckCircle2 className="twin-icon-sm twin-color-goal" />
                      <span>{kpi}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="twin-agentmodal-subsection">
                <h4 className="twin-agentmodal-subheading">Execution Milestones</h4>
                <div className="twin-agentmodal-list">
                  {fullDetails.goal.milestones.map((m) => (
                    <div key={m.id} className="twin-agentmodal-milestone">
                      <div className="twin-agentmodal-row-between">
                        <div className="twin-agentmodal-milestone-left">
                          {m.completed ? <CheckCircle2 className="twin-icon-sm twin-stat-positive" /> : <Clock className="twin-icon-sm twin-color-goal" />}
                          <span className={m.completed ? 'twin-agentmodal-milestone-done' : 'twin-agentmodal-milestone-pending'}>{m.title}</span>
                        </div>
                        <span className="twin-color-task twin-agentmodal-mono-sm">{m.progressPct}%</span>
                      </div>
                      <div className="twin-agentmodal-progress-track twin-agentmodal-progress-track--sm">
                        <div className={`twin-agentmodal-progress-fill ${m.completed ? 'twin-agentmodal-progress-fill--done' : 'twin-agentmodal-progress-fill--goal'}`} style={{ width: `${m.progressPct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* === TAB 3: CONTEXT === */}
          {activeTab === 'context' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-card">
                <div className="twin-agentmodal-row-between">
                  <span className="twin-agentmodal-label "><Brain className="twin-icon-md" /> Active LLM Context Window</span>
                  <span className="twin-color-context twin-agentmodal-mono-sm">
                    {fullDetails.context.activeSessionTokens.toLocaleString()} / {fullDetails.context.maxContextTokens.toLocaleString()} tokens
                  </span>
                </div>
                <div className="twin-agentmodal-progress-track">
                  <div
                    className="twin-agentmodal-progress-fill twin-agentmodal-progress-fill--context"
                    style={{ width: `${Math.min(100, (fullDetails.context.activeSessionTokens / fullDetails.context.maxContextTokens) * 100 * 50)}%` }}
                  />
                </div>
                <div className="twin-agentmodal-row-between twin-agentmodal-progress-footer">
                  <span>Working Memory: {fullDetails.context.memoryItemsCount} items</span>
                  <span>Injected RAG Chunks: {fullDetails.context.ragChunksLoaded} chunks</span>
                </div>
              </div>

              <div className="twin-agentmodal-card">
                <div className="twin-agentmodal-row-between">
                  <span className="twin-agentmodal-label twin-color-context">System Prompt (Base Instructions)</span>
                  <button type="button" onClick={() => handleCopy(fullDetails.context.systemPromptSummary, 'prompt')} className="twin-agentmodal-copy-link">
                    {copiedKey === 'prompt' ? <Check className="twin-icon-xs twin-stat-positive" /> : <Copy className="twin-icon-xs" />}
                    {copiedKey === 'prompt' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="twin-agentmodal-mono-box">{fullDetails.context.systemPromptSummary}</p>
              </div>

              <div className="twin-agentmodal-subsection">
                <h4 className="twin-agentmodal-subheading">Injected Environment Variables</h4>
                <div className="twin-agentmodal-envvar-grid">
                  {Object.entries(fullDetails.context.environmentVariables).map(([k, v]) => (
                    <div key={k} className="twin-agentmodal-envvar-item">
                      <span className="twin-color-context">{k}</span>
                      <span className="twin-agentmodal-envvar-value">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* === TAB 4: TOOLS === */}
          {activeTab === 'tools' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-row-between">
                <h4 className="twin-agentmodal-subheading"><Wrench className="twin-icon-sm twin-color-tools" /> Enabled MCP Servers & Tools ({fullDetails.tools.length})</h4>
                <span className="twin-stat-positive twin-agentmodal-mono-sm">All channels in READY state</span>
              </div>

              <div className="twin-agentmodal-list">
                {fullDetails.tools.map((tool) => (
                  <div key={tool.id} className="twin-agentmodal-tool-card">
                    <div className="twin-agentmodal-row-between">
                      <div className="twin-agentmodal-tool-left">
                        <span className="twin-agentmodal-chip twin-agentmodal-chip--cyan">{tool.type}</span>
                        <span className="twin-agentmodal-mono-bold">{tool.name}</span>
                      </div>
                      <span className="twin-agentmodal-status-pill">{tool.status}</span>
                    </div>
                    <p className="twin-agentmodal-desc">{tool.description}</p>
                    <div className="twin-agentmodal-row-between twin-agentmodal-tool-footer">
                      <span>Endpoint: {tool.endpointOrSchema}</span>
                      <span>Latency: <strong className="twin-color-task">{tool.latencyMs}ms</strong> • Invocations: {tool.callsCount}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === TAB 5: KNOWLEDGE === */}
          {activeTab === 'knowledge' && (
            <div className="twin-agentmodal-section">
              <h4 className="twin-agentmodal-subheading"><BookOpen className="twin-icon-sm twin-color-knowledge" /> Knowledge Bases & RAG Documentation ({fullDetails.knowledge.length})</h4>
              <div className="twin-agentmodal-list">
                {fullDetails.knowledge.map((kn) => (
                  <div key={kn.id} className="twin-agentmodal-tool-card">
                    <div className="twin-agentmodal-row-between">
                      <div className="twin-agentmodal-tool-left">
                        <span className="twin-agentmodal-chip twin-agentmodal-chip--indigo">{kn.type}</span>
                        <span className="twin-agentmodal-mono-bold">{kn.name}</span>
                      </div>
                      <span className="twin-color-task twin-agentmodal-mono-sm">{kn.tokensCount.toLocaleString()} tokens</span>
                    </div>
                    <p className="twin-agentmodal-desc">{kn.description}</p>
                    <div className="twin-agentmodal-row-between twin-agentmodal-tool-footer">
                      <span>Last indexed: {kn.lastUpdated}</span>
                      <span>Relevance: {Math.round((kn.relevanceScore || 0.95) * 100)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === TAB 6: MODEL === */}
          {activeTab === 'model' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-card twin-agentmodal-card--model">
                <div className="twin-agentmodal-row-between">
                  <span className="twin-agentmodal-label twin-color-model"><Bot className="twin-icon-md" /> LLM Inference Engine</span>
                  <span className="twin-agentmodal-chip twin-agentmodal-chip--cyan">{fullDetails.model.provider}</span>
                </div>
                <h4 className="twin-agentmodal-title-lg twin-agentmodal-mono-title">{fullDetails.model.modelName}</h4>
                <p className="twin-agentmodal-desc">State-of-the-art model optimized for deep autonomous reasoning and low latency.</p>
              </div>

              <div className="twin-agentmodal-stat-grid-4">
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Temperature</span>
                  <div className="twin-color-task twin-agentmodal-stat-value">{fullDetails.model.temperature}</div>
                  <span className="twin-agentmodal-stat-hint">High determinism</span>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Top-P</span>
                  <div className="twin-color-task twin-agentmodal-stat-value">{fullDetails.model.topP}</div>
                  <span className="twin-agentmodal-stat-hint">Nucleus sampling</span>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Max Tokens</span>
                  <div className="twin-color-task twin-agentmodal-stat-value">{fullDetails.model.maxOutputTokens.toLocaleString()}</div>
                  <span className="twin-agentmodal-stat-hint">Per response limit</span>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Inference Latency</span>
                  <div className="twin-stat-positive twin-agentmodal-stat-value">{fullDetails.model.avgInferenceLatencyMs} ms</div>
                  <span className="twin-agentmodal-stat-hint">Average p50</span>
                </div>
              </div>
            </div>
          )}

          {/* === TAB 7: COST === */}
          {activeTab === 'cost' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-card twin-agentmodal-card--cost twin-agentmodal-row-between">
                <div>
                  <span className="twin-agentmodal-label twin-stat-positive"><Coins className="twin-icon-md" /> Accumulated Session Cost</span>
                  <div className="twin-agentmodal-cost-value">${fullDetails.cost.totalCostUsd} <span className="twin-agentmodal-cost-unit">USD</span></div>
                </div>
                <div className="twin-agentmodal-cost-right">
                  <div>Throughput: <strong className="twin-stat-positive">{fullDetails.cost.avgTokensPerSec}</strong> t/s</div>
                  <div>Requests: <strong>{fullDetails.cost.requestsCount}</strong></div>
                </div>
              </div>

              <div className="twin-agentmodal-stat-grid-2">
                <div className="twin-agentmodal-stat-box">
                  <div className="twin-agentmodal-row-between twin-agentmodal-stat-title-row">
                    <span>Input Tokens (Prompt)</span>
                    <ArrowDownRight className="twin-icon-sm twin-color-task" />
                  </div>
                  <div className="twin-agentmodal-stat-value-lg">{fullDetails.cost.totalInputTokens.toLocaleString()}</div>
                  <span className="twin-agentmodal-stat-hint">~$0.00015 / 1K tokens</span>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <div className="twin-agentmodal-row-between twin-agentmodal-stat-title-row">
                    <span>Output Tokens (Completion)</span>
                    <ArrowUpRight className="twin-icon-sm twin-stat-positive" />
                  </div>
                  <div className="twin-agentmodal-stat-value-lg">{fullDetails.cost.totalOutputTokens.toLocaleString()}</div>
                  <span className="twin-agentmodal-stat-hint">~$0.00060 / 1K tokens</span>
                </div>
              </div>
            </div>
          )}

          {/* === TAB 8: STATE === */}
          {activeTab === 'state' && (
            <div className="twin-agentmodal-section">
              <div className="twin-agentmodal-vitals-grid">
                <div className="twin-agentmodal-stat-box">
                  <div className="twin-agentmodal-row-between twin-agentmodal-stat-title-row">
                    <span className="twin-agentmodal-vital-label twin-color-goal"><Battery className="twin-icon-sm" /> Energy</span>
                    <span className="twin-agentmodal-mono-bold">{fullDetails.state.energy}%</span>
                  </div>
                  <div className="twin-agentmodal-progress-track twin-agentmodal-progress-track--sm">
                    <div className="twin-agentmodal-progress-fill twin-agentmodal-progress-fill--goal" style={{ width: `${fullDetails.state.energy}%` }} />
                  </div>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <div className="twin-agentmodal-row-between twin-agentmodal-stat-title-row">
                    <span className="twin-agentmodal-vital-label twin-color-state"><Droplets className="twin-icon-sm" /> Restroom Need</span>
                    <span className="twin-agentmodal-mono-bold">{fullDetails.state.bladder}%</span>
                  </div>
                  <div className="twin-agentmodal-progress-track twin-agentmodal-progress-track--sm">
                    <div className="twin-agentmodal-progress-fill twin-agentmodal-progress-fill--state" style={{ width: `${fullDetails.state.bladder}%` }} />
                  </div>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <div className="twin-agentmodal-row-between twin-agentmodal-stat-title-row">
                    <span className="twin-agentmodal-vital-label twin-color-task"><Zap className="twin-icon-sm" /> Focus</span>
                    <span className="twin-agentmodal-mono-bold">{fullDetails.state.focus}%</span>
                  </div>
                  <div className="twin-agentmodal-progress-track twin-agentmodal-progress-track--sm">
                    <div className="twin-agentmodal-progress-fill twin-agentmodal-progress-fill--task" style={{ width: `${fullDetails.state.focus}%` }} />
                  </div>
                </div>
              </div>

              <div className="twin-agentmodal-telemetry-grid">
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Grid Location</span>
                  <div className="twin-agentmodal-telemetry-value">{fullDetails.state.currentLocationName}</div>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Distance Traveled</span>
                  <div className="twin-color-task twin-agentmodal-telemetry-value">
                    {((agent.distanceTraveledMeters || 0) < 1000)
                      ? `${(agent.distanceTraveledMeters || 0).toFixed(1)} m`
                      : `${((agent.distanceTraveledMeters || 0) / 1000).toFixed(2)} km`}
                  </div>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Processed Tasks</span>
                  <div className="twin-agentmodal-telemetry-value">{fullDetails.state.processedTasksCount} tasks</div>
                </div>
                <div className="twin-agentmodal-stat-box">
                  <span className="twin-agentmodal-stat-label">Avg SLA Latency</span>
                  <div className="twin-stat-positive twin-agentmodal-telemetry-value">{fullDetails.state.avgResponseTimeSec} s</div>
                </div>
              </div>

              <div className="twin-agentmodal-subsection">
                <h4 className="twin-agentmodal-subheading"><Cpu className="twin-icon-sm twin-color-goal" /> Real-time Thought Stream</h4>
                <div className="twin-agentmodal-thoughts-box">
                  {fullDetails.state.thoughts.map((t, idx) => (
                    <div key={idx} className="twin-agentmodal-thought-line">
                      <span className="twin-agentmodal-thought-time">[{t.time}]</span>
                      <span>{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* === TAB 9: DECISIONS === */}
          {activeTab === 'decisions' && (
            <div className="twin-agentmodal-section">
              <h4 className="twin-agentmodal-subheading"><Lightbulb className="twin-icon-sm twin-color-decisions" /> Autonomous Decision Log (Chain-of-Thought)</h4>
              <div className="twin-agentmodal-list">
                {fullDetails.decisions.map((dec) => (
                  <div key={dec.id} className="twin-agentmodal-tool-card">
                    <div className="twin-agentmodal-row-between">
                      <div className="twin-agentmodal-decision-header">
                        <span className="twin-color-decisions twin-agentmodal-mono-bold">[{dec.clockTime}]</span>
                        <span className="twin-agentmodal-mono-bold">{dec.trigger}</span>
                      </div>
                      <span className="twin-agentmodal-chip twin-agentmodal-chip--cyan">Confidence: {dec.confidencePct}%</span>
                    </div>
                    <div className="twin-agentmodal-reasoning-box">
                      <div className="twin-agentmodal-stat-label">Internal Reasoning:</div>
                      <p className="twin-agentmodal-desc">{dec.reasoning}</p>
                    </div>
                    <div className="twin-agentmodal-row-between twin-agentmodal-tool-footer">
                      <span>Action: <strong className="twin-stat-positive">{dec.chosenAction}</strong></span>
                      <span>Outcome: {dec.outcome}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === TAB 10: DEPENDENCIES === */}
          {activeTab === 'dependencies' && (
            <div className="twin-agentmodal-section">
              <h4 className="twin-agentmodal-subheading"><Network className="twin-icon-sm twin-color-dependencies" /> Inter-Agent Dependency Graph</h4>
              <div className="twin-agentmodal-list">
                {fullDetails.dependencies.map((dep, idx) => (
                  <div key={idx} className="twin-agentmodal-tool-card">
                    <div className="twin-agentmodal-row-between">
                      <div className="twin-agentmodal-tool-left">
                        <span className={`twin-agentmodal-chip twin-agentmodal-chip--${dep.relationship === 'depends_on' ? 'rose' : dep.relationship === 'feeds_into' ? 'emerald' : 'cyan'}`}>
                          {dep.relationship === 'depends_on' ? '◀ Depends on' : dep.relationship === 'feeds_into' ? '▶ Feeds into' : '◀▶ Collaborates with'}
                        </span>
                        <span className="twin-agentmodal-mono-bold">{dep.agentName}</span>
                        <span className="twin-agentmodal-stat-hint">({dep.agentRole})</span>
                      </div>
                      <span className="twin-agentmodal-stat-hint twin-agentmodal-mono-sm">{dep.protocol}</span>
                    </div>
                    <p className="twin-agentmodal-desc">{dep.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === CONTROL RÁPIDO Y TAREA IA (siempre visible al final) === */}
          <div className="twin-agentmodal-footer-panel">
            <div className="twin-agentmodal-control-card">
              <h4 className="twin-agentmodal-subheading"><Cpu className="twin-icon-sm twin-color-task" /> Immediate Agent Control</h4>
              <div className="twin-agentmodal-control-grid">
                <button type="button" onClick={() => onForceBreak(agent.id, 'coffee')} className="twin-agentmodal-control-btn twin-agentmodal-control-btn--amber">
                  <Coffee className="twin-icon-sm" /> Send to Breakroom
                </button>
                <button type="button" onClick={() => onForceBreak(agent.id, 'bathroom')} className="twin-agentmodal-control-btn twin-agentmodal-control-btn--pink">
                  <Droplets className="twin-icon-sm" /> Send to Restroom
                </button>
                <button type="button" onClick={() => onForceBreak(agent.id, 'work')} className="twin-agentmodal-control-btn twin-agentmodal-control-btn--emerald">
                  <Zap className="twin-icon-sm" /> Return to Workstation
                </button>
              </div>
            </div>

            <form onSubmit={handleTaskSubmit} className="twin-agentmodal-ai-form">
              <h4 className="twin-agentmodal-ai-form-title">
                <Sparkles className="twin-icon-sm twin-color-task" /> Run Ad-Hoc Task with Gemini AI for {agent.name}
              </h4>

              <input
                type="text"
                placeholder="Title or instruction (e.g., Refactor migration with savepoints)"
                value={customTaskTitle}
                onChange={(e) => setCustomTaskTitle(e.target.value)}
                className="twin-input twin-agentmodal-input"
              />

              <textarea
                rows={2}
                placeholder="Additional context or prompt parameters..."
                value={customContext}
                onChange={(e) => setCustomContext(e.target.value)}
                className="twin-textarea twin-agentmodal-input"
              />

              <button
                type="submit"
                disabled={isProcessingAI || !customTaskTitle.trim()}
                className={`twin-agentmodal-ai-submit ${isProcessingAI || !customTaskTitle.trim() ? 'twin-agentmodal-ai-submit--disabled' : ''}`}
              >
                {isProcessingAI ? (
                  <>
                    <span className="twin-agentmodal-spinner" />
                    Executing with Gemini AI...
                  </>
                ) : (
                  <>Process Prompt with AI</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};